import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {Column as ClColumn} from '@/products/flexible-web-toolkit/layout/column'
import type {Block} from '@/services/kontent-ai/types'
import type {BgColor} from '@/products/flexible-web-toolkit/layout/layout-common-types'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {getBackgroundTheme} from '@/products/flexible-web-toolkit/colors'
import {getContainerClass} from '../helpers'
import {spacingPadding} from '../../styles'

export type ColumnContentItem = IContentItem<{
	items: Elements.LinkedItemsElement
	layout: Elements.MultipleChoiceElement
	snippetContainerResize: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	snippetLayoutBackgroundColorColor: Elements.MultipleChoiceElement
	/** New color picker from snippet_layout_background_color snippet */
	snippetLayoutBackgroundColorBrandColor: Elements.CustomElement
}>

export const ColumnBlock: Block<ColumnContentItem> = ({
	block,
	BlockMapper,
	page,
	...context
}) => {
	const containerSizeOption =
		block.elements.snippetContainerResize[0]?.codename

	const containerSize = containerSizeOption
		? getContainerClass(containerSizeOption, 'container-wide')
		: 'container-wide'

	const componentSpacing = (() => {
		switch (block.elements.snippetSpacingSpacing[0]?.codename) {
			case 'default': {
				return spacingPadding.default
			}
			case 'top': {
				return spacingPadding.top
			}
			case 'bottom': {
				return spacingPadding.bottom
			}
			case 'none': {
				return spacingPadding.none
			}
			default: {
				return spacingPadding.none
			}
		}
	})()

	// Priority: new color picker > legacy dropdown > page config > fallback
	// fallbackColorId: 'primary' prevents a null brand producing !bg-null-light
	// when snippetColor === 'brand' and no color is configured.
	const {colorId: brand} = colorWithFallback({
		blockColor:
			block.elements.snippetLayoutBackgroundColorBrandColor || undefined,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	const legacySnippetColor: BgColor = (() => {
		const codename =
			block.elements.snippetLayoutBackgroundColorColor[0]?.codename
		if (codename) {
			return codename as BgColor
		}
		return 'none'
	})()

	// When the new color picker has a value, it takes precedence over the legacy dropdown
	// and directly sets the background color via backgroundThemes.
	// Also applies when no block-level color is set but page config provides a color.
	const hasNewPickerColor = Boolean(
		block.elements.snippetLayoutBackgroundColorBrandColor
	)
	const newPickerBgTheme =
		hasNewPickerColor && brand ? getBackgroundTheme(brand) : ''
	// If new picker is active, suppress legacy bgColor so it doesn't conflict
	const snippetColor: BgColor = hasNewPickerColor
		? 'none'
		: legacySnippetColor

	const liteBackground =
		legacySnippetColor === 'brand' && !hasNewPickerColor
			? `!bg-${brand}-light bg-default-light-background`
			: ''

	return (
		<ClColumn
			bgColor={snippetColor}
			className={clsx(
				componentSpacing,
				liteBackground,
				newPickerBgTheme,
				block.system.type
			)}
			containerClassName={containerSize}
		>
			<div className="w-[inherit]">
				{block.elements.items.map((item) => (
					<BlockMapper
						blocks={[item]}
						key={item.system.id}
						page={page}
						{...context}
					/>
				))}
			</div>
		</ClColumn>
	)
}
